import { useEffect, useState } from "react";
import Pagination from "../../components/common/pagination/Pagination";
import Title from "../../components/common/title/Title";
import Button from "../../components/common/button/Button";
import { getApi, putApi, deleteApi } from "../../services/api/globalApi";
import { useNavigate } from "react-router";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { RxDragHandleDots2 } from "react-icons/rx";
import {
  successMessage, errorMessage,
} from "../../utils/toastify-message/ToastifyMessage";

function SortableItem({ item, index, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <tr ref={setNodeRef} style={style}
      className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
    >
      <td className="px-2 sm:px-4 py-3">
        <button {...attributes} {...listeners} type="button" className="cursor-grab text-gray-500 hover:text-gray-700">
          <RxDragHandleDots2 size={18} />
        </button>
      </td>
      <td className="px-2 sm:px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{item.title}</td>
      <td className="px-2 sm:px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{item.desc}</td>
      <td className="px-2 sm:px-4 py-3 text-center text-sm">{index + 1}</td>
      <td className="px-2 sm:px-4 py-3">
        <div className="flex gap-2">
          <button onClick={() => onEdit(item)} className="text-sm font-medium text-blue-600 dark:text-blue-500 hover:underline">ویرایش</button>
          <button onClick={() => onDelete(item)} className="text-sm font-medium text-red-600 dark:text-red-500 hover:underline">حذف</button>
        </div>
      </td>
    </tr>
  );
}

export default function AffiliateBenefitList() {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [filter, setFilter] = useState({});
  const sensors = useSensors(useSensor(PointerSensor));
  const navigate = useNavigate();

  const fetchData = async (params) => {
    try {
      const response = await getApi({ path: "admin/affiliate-benefits", params: { ...params } });
      const dataWithIds = response?.data?.result?.data?.map((i) => ({ ...i, id: i._id }));
      setItems(dataWithIds || []);
      setPagination(response?.data?.result);
      setHasChanges(false);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => { fetchData({}); }, []);

  const filterDataHandler = (newParams) => {
    const allFilter = { ...filter, ...newParams };
    setFilter(allFilter);
    fetchData(allFilter);
  };

  const handleDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over?.id);
      setItems((prev) => arrayMove(prev, oldIndex, newIndex));
      setHasChanges(true);
    }
  };

  const savePriorities = async () => {
    try {
      const list = items.map((item, index) => ({ id: item._id, priority: items.length - index }));
      await putApi({ path: "admin/affiliate-benefits/update-priorities", body: { items: list } });
      successMessage("اولویت‌ها با موفقیت ذخیره شد");
      setHasChanges(false);
      fetchData({});
    } catch (error) {
      console.log(error);
      errorMessage("خطا در ذخیره اولویت‌ها");
    }
  };

  const handleDelete = async (item) => {
    if (window.confirm(`آیا از حذف "${item.title}" اطمینان دارید؟`)) {
      try {
        await deleteApi({ path: `admin/affiliate-benefits/${item._id}` });
        successMessage("مزیت با موفقیت حذف شد");
        fetchData(filter);
      } catch (error) {
        console.log(error);
        errorMessage("خطا در حذف");
      }
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-3xl p-6">
      <div className="flex justify-between items-center mb-6 gap-4">
        <Title title={"مزایای صفحه نمایندگی"} />
        <div className="flex gap-2">
          {hasChanges && (
            <Button onClick={savePriorities} text="ذخیره اولویت‌ها" color="primary" type="button" className="whitespace-nowrap min-w-fit" />
          )}
          <Button onClick={() => navigate("/add-affiliate-benefit")} text="افزودن مزیت" color="success" type="button" className="whitespace-nowrap min-w-fit" />
        </div>
      </div>

      <Pagination pagination={pagination} filterDataHandler={filterDataHandler} />

      <div className="relative overflow-x-auto shadow-md sm:rounded-lg mt-6">
        {items?.length ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <table className="w-full text-xs sm:text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 w-8"></th>
                                  <th className="px-2 sm:px-4 py-2 sm:py-3">عنوان</th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3">توضیح</th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-center w-16">اولویت</th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3">عملیات</th>
                </tr>
              </thead>
              <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                <tbody>
                  {items.map((item, index) => (
                    <SortableItem
                      key={item.id}
                      item={item}
                      index={index}
                      onEdit={(i) => navigate("/edit-affiliate-benefit", { state: i })}
                      onDelete={handleDelete}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </table>
          </DndContext>
        ) : (
          <div className="w-full flex items-center justify-center my-3 py-2 dark:text-white">
            <p>هیچ موردی ثبت نشده است.</p>
          </div>
        )}
      </div>

      <Pagination pagination={pagination} filterDataHandler={filterDataHandler} />
    </div>
  );
}